import React, { useState, useEffect, useRef, useCallback } from 'react';

function Planning() {
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [activePeopleInput, setActivePeopleInput] = useState(null);
  const [depSearch, setDepSearch] = useState('');
  const [activeDepInput, setActiveDepInput] = useState(null);
  const [collapsedTasks, setCollapsedTasks] = useState(new Set());
  const peopleInputRef = useRef(null);
  const depInputRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    fetch('/api/admin/planning')
      .then(r => r.json())
      .then(data => {
        setTasks(data.tasks || []);
        setPeople(data.people || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const save = useCallback((newTasks, newPeople) => {
    setSaving(true);
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      await fetch('/api/admin/planning', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tasks: newTasks, people: newPeople }),
      });
      setSaving(false);
    }, 500);
  }, []);

  const addTask = (parentId = null) => {
    const newTask = {
      id: `task-${Date.now()}`,
      title: '',
      completed: false,
      parentId,
      affectedPeople: [],
      dependsOn: [],
      order: tasks.filter(t => t.parentId === parentId).length,
    };
    const newTasks = [...tasks, newTask];
    setTasks(newTasks);
    setEditingTask(newTask.id);
    save(newTasks, people);
  };

  const updateTask = (taskId, updates) => {
    const newTasks = tasks.map(t => t.id === taskId ? { ...t, ...updates } : t);
    setTasks(newTasks);
    save(newTasks, people);
  };

  const deleteTask = (taskId) => {
    // Remove task and all its children, and remove from dependencies
    const idsToRemove = new Set();
    const collectChildren = (id) => {
      idsToRemove.add(id);
      tasks.filter(t => t.parentId === id).forEach(t => collectChildren(t.id));
    };
    collectChildren(taskId);
    const newTasks = tasks
      .filter(t => !idsToRemove.has(t.id))
      .map(t => ({ ...t, dependsOn: t.dependsOn.filter(d => !idsToRemove.has(d)) }));
    setTasks(newTasks);
    save(newTasks, people);
  };

  const toggleComplete = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    updateTask(taskId, { completed: !task.completed });
  };

  const addPersonToTask = (taskId, person) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.affectedPeople.includes(person)) return;
    const newPeople = people.includes(person) ? people : [...people, person];
    setPeople(newPeople);
    updateTask(taskId, { affectedPeople: [...task.affectedPeople, person] });
    save(tasks.map(t => t.id === taskId ? { ...t, affectedPeople: [...t.affectedPeople, person] } : t), newPeople);
    setPeopleSearch('');
  };

  const removePersonFromTask = (taskId, person) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    updateTask(taskId, { affectedPeople: task.affectedPeople.filter(p => p !== person) });
  };

  const addDependency = (taskId, depId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.dependsOn.includes(depId) || depId === taskId) return;
    updateTask(taskId, { dependsOn: [...task.dependsOn, depId] });
    setDepSearch('');
  };

  const removeDependency = (taskId, depId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    updateTask(taskId, { dependsOn: task.dependsOn.filter(d => d !== depId) });
  };

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (activePeopleInput && !e.target.closest('.people-search-container')) {
        setActivePeopleInput(null);
        setPeopleSearch('');
      }
      if (activeDepInput && !e.target.closest('.dep-search-container')) {
        setActiveDepInput(null);
        setDepSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activePeopleInput, activeDepInput]);

  useEffect(() => {
    if (activePeopleInput && peopleInputRef.current) peopleInputRef.current.focus();
  }, [activePeopleInput]);

  useEffect(() => {
    if (activeDepInput && depInputRef.current) depInputRef.current.focus();
  }, [activeDepInput]);

  const getTaskTitle = (id) => tasks.find(t => t.id === id)?.title || 'Unknown';

  // Check if all dependencies are completed
  const areDepsComplete = (task) => {
    if (!task.dependsOn.length) return true;
    return task.dependsOn.every(depId => {
      const dep = tasks.find(t => t.id === depId);
      return dep && dep.completed;
    });
  };

  // Get root tasks (no parent)
  const rootTasks = tasks
    .filter(t => !t.parentId)
    .sort((a, b) => a.order - b.order);

  const getChildren = (parentId) =>
    tasks.filter(t => t.parentId === parentId).sort((a, b) => a.order - b.order);

  const filteredPeople = peopleSearch
    ? people.filter(p => p.toLowerCase().includes(peopleSearch.toLowerCase()))
    : people;

  const toggleCollapse = (taskId) => {
    setCollapsedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const renderTask = (task, depth = 0) => {
    const children = getChildren(task.id);
    const isEditing = editingTask === task.id;
    const depsComplete = areDepsComplete(task);
    const blocked = !depsComplete && !task.completed;
    const hasChildren = children.length > 0;
    const isCollapsed = collapsedTasks.has(task.id);

    return (
      <div key={task.id} className="planning-task" style={{ marginLeft: depth * 24 }}>
        <div className={`planning-task-row ${task.completed ? 'completed' : ''} ${blocked ? 'blocked' : ''}`}>
          {hasChildren ? (
            <button
              className="planning-collapse-btn"
              onClick={() => toggleCollapse(task.id)}
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? '▶' : '▼'}
            </button>
          ) : (
            <span className="planning-collapse-spacer" />
          )}

          <input
            type="checkbox"
            checked={task.completed}
            onChange={() => toggleComplete(task.id)}
            className="planning-checkbox"
          />

          {isEditing ? (
            <input
              className="planning-title-input"
              value={task.title}
              onChange={e => updateTask(task.id, { title: e.target.value })}
              onBlur={() => setEditingTask(null)}
              onKeyDown={e => { if (e.key === 'Enter') setEditingTask(null); }}
              autoFocus
              placeholder="Task title..."
            />
          ) : (
            <span
              className="planning-title"
              onClick={() => setEditingTask(task.id)}
            >
              {task.title || 'Untitled task'}
              {isCollapsed && hasChildren && (
                <span className="planning-child-count"> ({children.length})</span>
              )}
            </span>
          )}

          <div className="planning-task-actions">
            <button onClick={() => addTask(task.id)} title="Add subtask" className="planning-btn">+</button>
            <button onClick={() => deleteTask(task.id)} title="Delete" className="planning-btn planning-btn-delete">&times;</button>
          </div>
        </div>

        {/* Affected people */}
        <div className="planning-meta">
          <div className="planning-people">
            {task.affectedPeople.map(person => (
              <span key={person} className="planning-person-chip">
                {person}
                <button onClick={() => removePersonFromTask(task.id, person)}>&times;</button>
              </span>
            ))}
            <div className="people-search-container">
              <button
                className="planning-btn planning-btn-small"
                onClick={() => {
                  setActivePeopleInput(activePeopleInput === task.id ? null : task.id);
                  setPeopleSearch('');
                }}
              >
                + Person
              </button>
              {activePeopleInput === task.id && (
                <div className="people-search-dropdown">
                  <input
                    ref={peopleInputRef}
                    type="text"
                    placeholder="Search person..."
                    value={peopleSearch}
                    onChange={e => setPeopleSearch(e.target.value)}
                  />
                  <div className="people-search-results">
                    {filteredPeople
                      .filter(p => !task.affectedPeople.includes(p))
                      .slice(0, 6)
                      .map(p => (
                        <div key={p} className="people-search-item" onClick={() => addPersonToTask(task.id, p)}>
                          {p}
                        </div>
                      ))}
                    {peopleSearch.trim() && !people.some(p => p.toLowerCase() === peopleSearch.trim().toLowerCase()) && (
                      <div
                        className="people-search-item people-search-add"
                        onClick={() => addPersonToTask(task.id, peopleSearch.trim())}
                      >
                        + Add "{peopleSearch.trim()}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dependencies */}
          {task.dependsOn.length > 0 && (
            <div className="planning-deps">
              <span className="planning-deps-label">Depends on:</span>
              {task.dependsOn.map(depId => {
                const dep = tasks.find(t => t.id === depId);
                return (
                  <span key={depId} className={`planning-dep-chip ${dep?.completed ? 'dep-done' : ''}`}>
                    {getTaskTitle(depId)}
                    <button onClick={() => removeDependency(task.id, depId)}>&times;</button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="dep-search-container">
            <button
              className="planning-btn planning-btn-small planning-btn-dep"
              onClick={() => {
                setActiveDepInput(activeDepInput === task.id ? null : task.id);
                setDepSearch('');
              }}
            >
              + Dependency
            </button>
            {activeDepInput === task.id && (
              <div className="people-search-dropdown">
                <input
                  ref={depInputRef}
                  type="text"
                  placeholder="Search task..."
                  value={depSearch}
                  onChange={e => setDepSearch(e.target.value)}
                />
                <div className="people-search-results">
                  {tasks
                    .filter(t => t.id !== task.id && !task.dependsOn.includes(t.id) && t.title)
                    .filter(t => !depSearch || t.title.toLowerCase().includes(depSearch.toLowerCase()))
                    .slice(0, 6)
                    .map(t => (
                      <div key={t.id} className="people-search-item" onClick={() => addDependency(task.id, t.id)}>
                        {t.title}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {!isCollapsed && children.map(child => renderTask(child, depth + 1))}
      </div>
    );
  };

  // Build people-to-tasks mapping
  const peopleTasks = {};
  people.forEach(p => { peopleTasks[p] = []; });
  tasks.forEach(t => {
    t.affectedPeople.forEach(p => {
      if (!peopleTasks[p]) peopleTasks[p] = [];
      peopleTasks[p].push(t);
    });
  });

  if (loading) return <div className="admin-container"><p>Loading...</p></div>;

  return (
    <div className="planning-container">
      <section className="planning-section">
        <div className="planning-section-header">
          <h2>Wedding Timeline</h2>
          {saving && <span className="saving-indicator">Saving...</span>}
        </div>

        <div className="planning-tasks">
          {rootTasks.map(task => renderTask(task))}
        </div>

        <button className="planning-add-root" onClick={() => addTask(null)}>
          + Add Task
        </button>
      </section>

      <section className="planning-section">
        <h2>People & Responsibilities</h2>
        <div className="planning-people-grid">
          {Object.entries(peopleTasks)
            .filter(([, taskList]) => taskList.length > 0)
            .map(([person, taskList]) => (
              <div key={person} className="planning-person-card">
                <div className="planning-person-card-name">{person}</div>
                <div className="planning-person-tasks">
                  {taskList.map(t => (
                    <div key={t.id} className={`planning-person-task-row ${t.completed ? 'task-done' : ''}`}>
                      <input
                        type="checkbox"
                        checked={t.completed}
                        onChange={() => toggleComplete(t.id)}
                      />
                      <div className="planning-person-task-title">{t.title || 'Untitled'}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}

export default Planning;
